import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Users, UserPlus, Clock, Calendar, FileText, Award,
  DollarSign, Briefcase, GraduationCap, Mail, Phone,
  Search, Filter, Download, Plus, Building2, ChevronRight,
  Check, AlertTriangle, FileCheck, BarChart2, Target,
  BookOpen, UserCheck
} from 'lucide-react';
import { motion } from 'framer-motion';
import { employeeData, attendanceData, payrollData } from './data/mockData';
import { LineChart, BarChart } from 'recharts';
import AttendanceModule from './components/AttendanceModule';
import EmployeeModule from './components/EmployeeModule';
import LeaveModule from './components/LeaveModule';
import PayrollModule from './components/PayrollModule';
import PerformanceModule from './components/PerformanceModule';
import TrainingModule from './components/TrainingModule';
import RecruitmentModule from './components/RecruitmentModule';

// Components
const QuickActionButton = ({ icon: Icon, label, onClick, color = "indigo" }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={`flex items-center gap-3 p-4 bg-${color}-50 rounded-xl hover:bg-${color}-100 transition-colors w-full`}
  >
    <Icon className={`w-5 h-5 text-${color}-600`} />
    <span className={`text-${color}-600 font-medium`}>{label}</span>
  </motion.button>
);

const StatCard = ({ title, value, icon: Icon, trend, color }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all"
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <h3 className="text-2xl font-bold text-gray-800 mt-1">{value}</h3>
        {trend && (
          <p className={`text-sm mt-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}% vs last month
          </p>
        )}
      </div>
      <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </motion.div>
);

const OverviewContent = ({ isMobile }) => {
  return (
    <div className="space-y-6">
      {/* Department Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-sm"
        >
          <h3 className="text-lg font-semibold mb-4">Department Distribution</h3>
          <div className="space-y-4">
            {employeeData.departments.map((dept, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-indigo-600" />
                  <span className="text-sm font-medium">{dept.name}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-500">{dept.count} employees</span>
                  <span className="text-sm font-medium">
                    {((dept.count / employeeData.stats.totalEmployees) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Recent Activities */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-sm"
        >
          <h3 className="text-lg font-semibold mb-4">Recent Activities</h3>
          <div className="space-y-4">
            {employeeData.recentActivities.map((activity) => (
              <div key={activity.id} className="flex items-start gap-4">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center
                  ${activity.type === 'new_hire' ? 'bg-green-100' :
                    activity.type === 'leave_request' ? 'bg-amber-100' :
                    'bg-blue-100'}
                `}>
                  {activity.type === 'new_hire' ? (
                    <UserPlus className="w-4 h-4 text-green-600" />
                  ) : activity.type === 'leave_request' ? (
                    <Calendar className="w-4 h-4 text-amber-600" />
                  ) : (
                    <FileCheck className="w-4 h-4 text-blue-600" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {activity.employee}
                  </p>
                  <p className="text-sm text-gray-500">
                    {activity.type === 'new_hire' 
                      ? `Joined as ${activity.position} in ${activity.department}`
                      : activity.type === 'leave_request'
                      ? `Requested ${activity.duration} leave`
                      : `Completed review with rating ${activity.rating}`
                    }
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(activity.date).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Upcoming Events */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl p-6 shadow-sm"
      >
        <h3 className="text-lg font-semibold mb-4">Upcoming Events</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {employeeData.upcomingEvents.map((event) => (
            <div key={event.id} className="p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3 mb-3">
                {event.type === 'review' ? (
                  <Target className="w-5 h-5 text-purple-600" />
                ) : event.type === 'training' ? (
                  <BookOpen className="w-5 h-5 text-blue-600" />
                ) : (
                  <UserCheck className="w-5 h-5 text-green-600" />
                )}
                <span className="text-sm font-medium">{event.title}</span>
              </div>
              <p className="text-sm text-gray-500">
                {event.employee || `${event.participants} participants`}
              </p>
              <p className="text-xs text-gray-400 mt-2">
                {new Date(event.date).toLocaleDateString()}
              </p>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

const HRDashboard = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeModule, setActiveModule] = useState('overview');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const modules = [
    { id: 'overview', label: 'Overview', icon: BarChart2 },
    { id: 'employees', label: 'Employees', icon: Users },
    { id: 'attendance', label: 'Attendance', icon: Clock },
    { id: 'leave', label: 'Leave', icon: Calendar },
    { id: 'payroll', label: 'Payroll', icon: DollarSign },
    { id: 'performance', label: 'Performance', icon: Target },
    { id: 'training', label: 'Training', icon: BookOpen },
    { id: 'recruitment', label: 'Recruitment', icon: UserPlus }
  ];

  const quickStats = [
    { title: 'Total Employees', value: '248', icon: Users, color: 'bg-indigo-600' },
    { title: 'Present Today', value: '225', icon: Check, trend: 2, color: 'bg-green-600' },
    { title: 'On Leave', value: '12', icon: Calendar, color: 'bg-amber-600' },
    { title: 'Open Positions', value: '8', icon: Briefcase, color: 'bg-purple-600' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Mobile Header */}
      {isMobile && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">HR Dashboard</h1>
          <p className="text-gray-500">Managing 248 employees</p>
        </div>
      )}

      {/* Desktop Header */}
      {!isMobile && (
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">HR Dashboard</h1>
            <p className="text-gray-500">Managing 248 employees • 8 open positions</p>
          </div>
          <div className="flex gap-3">
            <QuickActionButton
              icon={Download}
              label="Export Reports"
              onClick={() => {}}
              color="gray"
            />
            <QuickActionButton
              icon={UserPlus}
              label="Add Employee"
              onClick={() => navigate('/hr/employees/new')}
            />
          </div>
        </div>
      )}

      {/* Module Navigation */}
      <div className="mb-8 overflow-x-auto">
        <div className={`flex ${isMobile ? 'gap-2' : 'gap-4'} pb-2`}>
          {modules.map((module) => (
            <button
              key={module.id}
              onClick={() => setActiveModule(module.id)}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap
                ${activeModule === module.id 
                  ? 'bg-indigo-600 text-white shadow-lg' 
                  : 'bg-white text-gray-600 hover:bg-gray-50'}
                transition-all duration-200
              `}
            >
              <module.icon className="w-5 h-5" />
              <span className="font-medium">{module.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {quickStats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      {/* Main Content Area */}
      <div className="space-y-6">
        {activeModule === 'overview' && <OverviewContent isMobile={isMobile} />}
        {activeModule === 'attendance' && <AttendanceModule isMobile={isMobile} />}
        {activeModule === 'employees' && <EmployeeModule isMobile={isMobile} />}
        {activeModule === 'leave' && <LeaveModule isMobile={isMobile} />}
        {activeModule === 'payroll' && <PayrollModule isMobile={isMobile} />}
        {activeModule === 'performance' && <PerformanceModule isMobile={isMobile} />}
        {activeModule === 'training' && <TrainingModule isMobile={isMobile} />}
        {activeModule === 'recruitment' && <RecruitmentModule isMobile={isMobile} />}
        {/* Other modules will go here */}
      </div>
    </div>
  );
};

export default HRDashboard;